import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import confetti from 'canvas-confetti';
import axiosInstance from '../Authservices/axiosInstance.js';
import './SigninForm.css';

const Signin = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotPasswordVisible, setForgotPasswordVisible] = useState(false);
  const [userIdForReset, setUserIdForReset] = useState('');

  // ✅ Birthday popup states
  const [showBirthday, setShowBirthday] = useState(false);
  const [birthdayName, setBirthdayName] = useState('');
  const pendingRoleRef = useRef('');
  const birthdayTimerRef = useRef(null);

  const navigate = useNavigate();

  useEffect(() => {
    const checkSessionExpiry = () => {
      const expiryTime = localStorage.getItem('expiry');
      if (expiryTime && Date.now() > expiryTime) {
        handleLogout();
      }
    };

    const interval = setInterval(checkSessionExpiry, 60000);
    return () => {
      clearInterval(interval);
      if (birthdayTimerRef.current) clearTimeout(birthdayTimerRef.current);
    };
  }, []);

  const handleLogout = () => {
    localStorage.clear();
    navigate('/signin');
  };

  // ✅ Helper: check if today is the user's birthday (timezone-safe)
  const isBirthdayToday = (birthday) => {
    if (!birthday) return false;
    const bday = new Date(birthday);
    if (isNaN(bday.getTime())) return false;

    const today = new Date();

    const matchUTC =
      bday.getUTCDate() === today.getDate() &&
      bday.getUTCMonth() === today.getMonth();

    const matchLocal =
      bday.getDate() === today.getDate() &&
      bday.getMonth() === today.getMonth();

    return matchUTC || matchLocal;
  };

  // ✅ Enhanced Confetti with emoji shapes + multi-wave
  const fireConfetti = () => {
    const colors = ['#ff4d6d', '#ffd166', '#06d6a0', '#118ab2', '#c77dff', '#ff8fab'];

    let heartShape, cakeShape, starShape, partyShape;
    try {
      heartShape = confetti.shapeFromText({ text: '💖', scalar: 2 });
      cakeShape  = confetti.shapeFromText({ text: '🎂', scalar: 2 });
      starShape  = confetti.shapeFromText({ text: '⭐', scalar: 2 });
      partyShape = confetti.shapeFromText({ text: '🎉', scalar: 2 });
    } catch (e) {
      console.warn('Emoji confetti not supported, falling back to plain confetti');
    }

    // 1. Big initial burst
    confetti({
      particleCount: 180,
      spread: 100,
      startVelocity: 55,
      origin: { y: 0.6 },
      colors,
      zIndex: 9999,
    });

    // 2. Emoji shower
    if (heartShape && cakeShape && starShape && partyShape) {
      confetti({
        particleCount: 40,
        spread: 130,
        startVelocity: 40,
        origin: { y: 0.55 },
        shapes: [heartShape, cakeShape, starShape, partyShape],
        scalar: 2.2,
        zIndex: 9999,
      });
    }

    // 3. Side cannons for ~5 seconds
    const duration = 5000;
    const end = Date.now() + duration;
    (function frame() {
      confetti({
        particleCount: 5,
        angle: 60,
        spread: 65,
        startVelocity: 50,
        origin: { x: 0, y: 0.7 },
        colors,
        zIndex: 9999,
      });
      confetti({
        particleCount: 5,
        angle: 120,
        spread: 65,
        startVelocity: 50,
        origin: { x: 1, y: 0.7 },
        colors,
        zIndex: 9999,
      });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();

    // 4. Second emoji explosion after 1.5s
    setTimeout(() => {
      if (heartShape && starShape) {
        confetti({
          particleCount: 25,
          spread: 360,
          startVelocity: 45,
          ticks: 250,
          origin: { x: 0.5, y: 0.35 },
          shapes: [heartShape, starShape],
          scalar: 2,
          zIndex: 9999,
        });
      }
    }, 1500);

    // 5. Third wave — big random burst at 3s
    setTimeout(() => {
      confetti({
        particleCount: 120,
        spread: 160,
        startVelocity: 45,
        origin: { x: Math.random(), y: Math.random() * 0.4 + 0.3 },
        colors,
        zIndex: 9999,
      });
    }, 3000);
  };

  // ✅ Central navigation function
  const proceedToDashboard = (userRole) => {
    switch (userRole) {
      case 'Admin':
        navigate('/admin-dashboard');
        break;
      case 'Employee':
        navigate('/team-chart');
        break;
      case 'HR':
        navigate('/hr-dashboard');
        break;
      case 'Accounts':
        navigate('/accounts-dashboard');
        break;
      default:
        setError('Access Denied: Invalid role.');
        localStorage.clear();
    }
  };

  // ✅ Birthday continue handler
  const handleBirthdayContinue = () => {
    if (birthdayTimerRef.current) clearTimeout(birthdayTimerRef.current);
    setShowBirthday(false);
    proceedToDashboard(pendingRoleRef.current);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    setForgotPasswordVisible(false);

    if (!username || !password || !role) {
      setError('Please fill in all fields.');
      setLoading(false);
      return;
    }

    try {
      console.log('🔐 Login attempt for:', username);

      const response = await axiosInstance.post('user/signin', {
        username,
        password,
      });

      console.log('✅ Login Response:', response.data);

      const {
        userId,
        name,
        token,
        refreshToken,
        role: userRole,
        username: responseUsername,
        departmentId,
        department,
        departmentType,
        departmentTypes = [],
        email,
        phone,
        jobTitle,
        salary,
        profilePhoto,
        gender,
        maritalstatus,
        status,
        birthday,
      } = response.data;

      if (userRole !== role) {
        setError(`Please select "${userRole}" role to login.`);
        setLoading(false);
        return;
      }

      localStorage.clear();

      localStorage.setItem('userId', userId);
      localStorage.setItem('name', name);
      localStorage.setItem('username', responseUsername || username);
      localStorage.setItem('accessToken', token);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('role', userRole);

      localStorage.setItem('departmentId', departmentId || '');
      localStorage.setItem('department', department || 'N/A');
      localStorage.setItem('departmentType', departmentType || '');
      localStorage.setItem('departmentTypes', JSON.stringify(departmentTypes));

      localStorage.setItem('email', email || '');
      localStorage.setItem('phone', phone || '');
      localStorage.setItem('jobTitle', jobTitle || '');
      localStorage.setItem('salary', salary || '');
      localStorage.setItem('profilePhoto', profilePhoto || '');
      localStorage.setItem('gender', gender || '');
      localStorage.setItem('maritalstatus', maritalstatus || '');
      localStorage.setItem('status', status || '');
      localStorage.setItem('birthday', birthday || '');

      localStorage.setItem('expiry', Date.now() + 3600000);

      localStorage.setItem('shouldShowUnseenDialog', 'true');
      localStorage.setItem('lastLoginTime', Date.now().toString());

      // ========================================================
      // 🎂 BIRTHDAY CHECK
      // ========================================================
      if (isBirthdayToday(birthday)) {
        console.log('🎉 It is the user\'s birthday today!');
        pendingRoleRef.current = userRole;
        setBirthdayName(name);
        setShowBirthday(true);
        fireConfetti();

        birthdayTimerRef.current = setTimeout(() => {
          setShowBirthday(false);
          proceedToDashboard(userRole);
        }, 8000);
      } else {
        proceedToDashboard(userRole);
      }
    } catch (err) {
      console.error('❌ Error during sign-in:', err);
      if (err.response) {
        const message = err.response.data.message || 'Invalid credentials';
        setError(message);
        if (message === 'Incorrect password' && err.response.data.userId) {
          setForgotPasswordVisible(true);
          setUserIdForReset(err.response.data.userId);
        }
      } else if (err.request) {
        setError('No response from server. Check your connection.');
      } else {
        setError('Error: ' + err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page-container">
      <div className="auth-image-wrapper">
        <img src="./logo-ems.jpg" alt="Authentication" className="auth-image" />
      </div>

      <form className="auth-form-container" onSubmit={handleSubmit} autoComplete="off">
        <h2 className="auth-form-title">Sign In</h2>

        <input type="text" name="fakeuser" autoComplete="username" style={{ display: 'none' }} />
        <input type="password" name="fakepass" autoComplete="new-password" style={{ display: 'none' }} />

        {loading && <p className="auth-loading">Loading...</p>}
        {error && <p className="auth-error" style={{ color: 'red' }}>{error}</p>}

        <div className="auth-input-group">
          <div className="fancy-select-wrapper">
            <select
              className="fancy-select"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              required
            >
              <option value="" disabled>Select your role</option>
              <option value="Admin">Admin</option>
              <option value="Employee">Employee</option>
              <option value="HR">HR</option>
              <option value="Accounts">Accounts</option>
            </select>
          </div>
        </div>

        <div className="auth-input-group">
          <input
            className="auth-input"
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Enter Username"
            autoComplete="off"
            required
          />
        </div>

        <div className="auth-input-group">
          <input
            className="auth-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="******"
            autoComplete="new-password"
            required
          />
        </div>

        <button className="auth-signin-button" type="submit" disabled={loading}>
          {loading ? 'Signing In...' : 'Sign In'}
        </button>

        <div className={`forgot-password-wrapper ${forgotPasswordVisible ? 'visible' : ''}`}>
          <p className="forgot-password-link">
            Forgot Password?{' '}
            <NavLink to={`/password-reset?userId=${userIdForReset}`}>Click Here!</NavLink>
          </p>
        </div>
      </form>

      {/* 🎂 BIRTHDAY OVERLAY */}
      {showBirthday && (
        <div className="birthday-overlay">
          {/* Floating balloons background 🎈 */}
          <div className="balloon balloon-1">🎈</div>
          <div className="balloon balloon-2">🎈</div>
          <div className="balloon balloon-3">🎈</div>
          <div className="balloon balloon-4">🎈</div>

          <div className="birthday-card">
            {/* 🐻 Cute Bear Character peeking from top */}
            <svg className="cute-character" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
              <circle cx="28" cy="32" r="17" fill="#d9a679" />
              <circle cx="92" cy="32" r="17" fill="#d9a679" />
              <circle cx="28" cy="32" r="9" fill="#f5b8a0" />
              <circle cx="92" cy="32" r="9" fill="#f5b8a0" />

              <circle cx="60" cy="68" r="42" fill="#e8b895" />

              <path d="M 40 65 Q 46 58 52 65" stroke="#3a2a20" strokeWidth="3.5" fill="none" strokeLinecap="round" />
              <path d="M 68 65 Q 74 58 80 65" stroke="#3a2a20" strokeWidth="3.5" fill="none" strokeLinecap="round" />

              <ellipse cx="33" cy="78" rx="8" ry="4.5" fill="#ff9aa8" opacity="0.75" />
              <ellipse cx="87" cy="78" rx="8" ry="4.5" fill="#ff9aa8" opacity="0.75" />

              <ellipse cx="60" cy="78" rx="5" ry="4" fill="#3a2a20" />

              <path d="M 50 86 Q 60 96 70 86" stroke="#3a2a20" strokeWidth="3" fill="none" strokeLinecap="round" />

              <path d="M 60 8 L 47 36 L 73 36 Z" fill="#ff4d6d" stroke="#c81d43" strokeWidth="1" />
              <circle cx="60" cy="8" r="4" fill="#ffd166" />
              <circle cx="55" cy="22" r="2" fill="#fff" />
              <circle cx="65" cy="22" r="2" fill="#fff" />
              <circle cx="60" cy="28" r="2" fill="#fff" />
            </svg>


            <h2 className="birthday-title">
              Happy Birthday, {birthdayName}!
            </h2>

            <p className="birthday-tag">✨ It's your special day! ✨</p>

            {/* ✅ ONE-LINE MESSAGE */}
            <p className="birthday-message">
              Wishing you a day full of laughter, love, and endless cake! 🍰✨🌈
            </p>

            <button className="birthday-button" onClick={handleBirthdayContinue}>
              Continue to Dashboard →
            </button>

            <p className="birthday-auto">Auto-redirecting in a few seconds…</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Signin;